/*!
=========================================================
* Argon Dashboard PRO React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

const { Chart, BarElement, Tooltip, Legend, CategoryScale, LinearScale, Title } = require('chart.js');

// Register required elements and scales for Chart.js v3+
Chart.register(BarElement, Tooltip, Legend, CategoryScale, LinearScale, Title);

// Override the 'bar' element's draw method to create rounded bars
const Rectangle = Chart.registry.getElement('bar');

if (Rectangle) {
  Rectangle.prototype.draw = function () {
    const ctx = this.ctx;
    const vm = this; // This element

    const { x, y, base, width, height, options } = vm;
    const cornerRadius = 6;
    const borderWidth = options.borderWidth;

    let left, right, top, bottom, signX, signY, borderSkipped;

    if (!options.horizontal) {
      // vertical bar
      left = x - width / 2;
      right = x + width / 2;
      top = y;
      bottom = base;
      signX = 1;
      signY = bottom > top ? 1 : -1;
      borderSkipped = options.borderSkipped || 'bottom';
    } else {
      // horizontal bar
      left = base;
      right = x;
      top = y - height / 2;
      bottom = y + height / 2;
      signX = right > left ? 1 : -1;
      signY = 1;
      borderSkipped = options.borderSkipped || 'left';
    }

    // Adjust border width if needed
    let bw = borderWidth || 0;
    // limit border width to bar size
    const barSize = Math.min(Math.abs(left - right), Math.abs(top - bottom));
    if (bw > barSize) {
      bw = barSize;
    }
    const halfStroke = bw / 2;

    const borderLeft = left + (borderSkipped !== 'left' ? halfStroke * signX : 0);
    const borderRight = right + (borderSkipped !== 'right' ? -halfStroke * signX : 0);
    const borderTop = top + (borderSkipped !== 'top' ? halfStroke * signY : 0);
    const borderBottom = bottom + (borderSkipped !== 'bottom' ? -halfStroke * signY : 0);

    if (borderLeft !== borderRight) {
      top = borderTop;
      bottom = borderBottom;
    }
    if (borderTop !== borderBottom) {
      left = borderLeft;
      right = borderRight;
    }

    // Width and height after border adjustment
    const barWidth = right - left;
    const barHeight = bottom - top;

    ctx.beginPath();
    ctx.fillStyle = options.backgroundColor;
    ctx.strokeStyle = options.borderColor;
    ctx.lineWidth = bw;

    // Define corners clockwise starting from bottom-left
    const corners = [
      [left, bottom],
      [left, top],
      [right, top],
      [right, bottom]
    ];

    const borders = ['bottom', 'left', 'top', 'right'];
    let startCorner = borders.indexOf(borderSkipped);
    if (startCorner === -1) startCorner = 0;

    function cornerAt(index) {
      return corners[(startCorner + index) % 4];
    }

    // Draw rounded rectangle path
    const radius = Math.min(cornerRadius, Math.abs(barHeight) / 2, Math.abs(barWidth) / 2);

    let corner = cornerAt(0);
    ctx.moveTo(corner[0], corner[1]);

    for (let i = 1; i < 4; i++) {
      corner = cornerAt(i);
      const nextCorner = cornerAt(i + 1 === 4 ? 0 : i + 1);

      if (i === 1) {
        // top side
        ctx.lineTo(corner[0] - radius, corner[1]);
        ctx.quadraticCurveTo(corner[0], corner[1], corner[0], corner[1] + radius);
      } else if (i === 2) {
        // right side
        ctx.lineTo(corner[0], corner[1] + radius);
        ctx.quadraticCurveTo(corner[0], corner[1], corner[0] - radius, corner[1]);
      } else if (i === 3) {
        // bottom side
        ctx.lineTo(corner[0] + radius, corner[1]);
        ctx.quadraticCurveTo(corner[0], corner[1], corner[0], corner[1] - radius);
      } else {
        // left side
        ctx.lineTo(corner[0], corner[1] - radius);
        ctx.quadraticCurveTo(corner[0], corner[1], corner[0] + radius, corner[1]);
      }
    }

    ctx.closePath();
    ctx.fill();

    if (bw) {
      ctx.stroke();
    }
  };
}

const mode = 'light'; // or 'dark', can be dynamic
const fonts = {
  base: 'Open Sans',
};

// Colors
const colors = {
  gray: {
    100: '#f6f9fc',
    200: '#e9ecef',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#8898aa',
    700: '#525f7f',
    800: '#32325d',
    900: '#212529',
  },
  theme: {
    default: '#172b4d',
    primary: '#5e72e4',
    secondary: '#f4f5f7',
    info: '#11cdef',
    success: '#2dce89',
    danger: '#f5365c',
    warning: '#fb6340',
  },
  black: '#12263F',
  white: '#FFFFFF',
  transparent: 'transparent',
};

// Chart.js global options
function chartOptions() {
  // Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    color: mode === 'dark' ? colors.gray[700] : colors.gray[600],
    font: {
      family: fonts.base,
      size: 13,
    },
    layout: {
      padding: 0,
    },
    plugins: {
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 16,
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
      },
    },
    elements: {
      point: {
        radius: 0,
        backgroundColor: colors.theme.primary,
      },
      line: {
        tension: 0.4,
        borderWidth: 4,
        borderColor: colors.theme.primary,
        backgroundColor: colors.transparent,
        borderCapStyle: 'round',
      },
      rectangle: {
        backgroundColor: colors.theme.warning,
      },
      arc: {
        backgroundColor: colors.theme.primary,
        borderColor: mode === 'dark' ? colors.gray[800] : colors.white,
        borderWidth: 4,
      },
    },
    scales: {
      y: {
        grid: {
          borderDash: [2],
          color: mode === 'dark' ? colors.gray[900] : colors.gray[300],
          drawBorder: false,
          drawTicks: false,
          lineWidth: 1,
          zeroLineWidth: 1,
          zeroLineColor: mode === 'dark' ? colors.gray[900] : colors.gray[300],
          zeroLineBorderDash: [2],
        },
        ticks: {
          beginAtZero: true,
          padding: 10,
          callback: function (value) {
            if (!(value % 10)) {
              return value;
            }
          },
        },
      },
      x: {
        grid: {
          drawBorder: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          padding: 20,
        },
      },
    },
  };

  return options;
}

// Parse global options helper
function parseOptions(parent, options) {
  for (const item in options) {
    if (typeof options[item] !== 'object') {
      parent[item] = options[item];
    } else {
      parseOptions(parent[item], options[item]);
    }
  }
}

module.exports = {
  chartOptions,
  parseOptions,
};
